import { Button } from '@shared';
import { cn } from '@shared/lib';
import { t } from '@/i18n';

import type { FinancialCalendarEvent } from '@/entities/financial-calendar';
import {
  getEventCategoryBadgeClass,
  getEventCategoryLabel,
  getCountryFlag,
  eventTimeLabel,
  getEventTitle,
} from '../model/utils';
import { ImportanceBadge } from './ImportanceBadge';
import { ReleaseBadge } from './ReleaseBadge';
import { formatCalendarValue } from '../model/actual-display';

interface EventCardProps {
  event: FinancialCalendarEvent;
  isSelected?: boolean;
  onClick?: (event: FinancialCalendarEvent) => void;
}

export const EventCard = ({ event, isSelected = false, onClick }: EventCardProps) => {
  // TRAD-2897 — 시각 정밀도(필드 없음 = 종전 «00:00 → 종일»). 상세 패널·드로어와 같은 함수.
  const time = eventTimeLabel(event, { withZone: false });
  const isCrypto = event.category === 'CRYPTO';
  // TRAD-2918 — forecast는 정책상(2026-07-30 davidyoon 승인) 무료 API로 못 채워 항상
  // null이다(backend 실측: INDICATOR 33건 전수 forecast 있는 건 0건). hasValues 판정에서도
  // 뺀다 — forecast만 값이 있는 상태는 정책상 존재할 수 없어 여기 남겨도 아무 분기도 안 타지만,
  // "이 필드도 본다"는 착각을 남기지 않기 위해 명시적으로 제외한다.
  const hasValues = event.actual !== null || event.previous !== null;

  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => onClick?.(event)}
      className={cn(
        'block h-auto w-full rounded-lg border p-3 shadow-none',
        'hover:bg-accent/50',
        isSelected ? 'border-primary bg-accent/30' : 'border-border bg-card',
      )}
    >
      {/* Top row: time · flag · importance */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
        <span className="font-medium tabular-nums">{time}</span>
        {/* 국기는 TRAD-2310 의 `getCountryFlag`(null·OTHER 를 🌐 로 흡수) 유지 —
            2127 브랜치의 인라인 분기보다 늦게 들어온 정본이다. */}
        <span>{getCountryFlag(event.country)}</span>
        <ImportanceBadge impact={event.impact} />
        <span
          className={cn(
            'ml-auto rounded-full px-2 py-0.5 text-2xs font-medium',
            getEventCategoryBadgeClass(event.category),
          )}
        >
          {getEventCategoryLabel(event.category)}
        </span>
        {/* TRAD-2899 — 종전 `event.confirmed && «발표완료»` → 발표 상태(필드 없음이면 종전과 같다). */}
        <ReleaseBadge event={event} />
      </div>

      {/* Title */}
      <p className="text-sm font-medium leading-snug line-clamp-2">{getEventTitle(event)}</p>

      {/* Values row: actual / previous */}
      {/* TRAD-2751 — 하드코딩 한글 3개 제거. 원래는 financial_calendar.values.*(값 뒤에 "값"이
          붙는 EventDetailPanel/Drawer의 상세 라벨)과 문구가 달라(카드는 "실제", 상세는 "실제값")
          재사용하지 않고 신규 키(financial_calendar.card.*)로 뺐다 — 기존 화면 문구를 그대로
          보존하는 것이 이관 원칙이라, 값을 맞추려면 카피 변경이 되어 planner 검수 범위였다.
          🔴 TRAD-2909/2917 — planner가 "실제"/"이전"(접미사 없음)으로 확정하면서 그 검수가
          끝났다. EventDetailPanel/Drawer도 이제 이 `card.actual`/`card.previous`를 쓴다
          (values.actual/values.previous 키는 삭제) — 한 화면이 목록/상세에서 다른 말을 하던
          것을 해소. `card.forecast`는 그대로 안 건드림(TRAD-2918 정정: forecast 칸 자체가
          삭제 예정이라 지금 통일하면 곧 지울 것에 손대는 일이다). */}
      {/* TRAD-2918 — forecast 칸 제거(davidyoon 정책 확정 "actual/previous 2칸만", designer
          (가) 채택). 응답 필드(event.forecast)는 안 지운다 — 유료 API 전환 시 되돌릴 수 있게
          UI만 뺀다. `financial_calendar.card.forecast` 카탈로그 키는 소비처 0이 되지만
          삭제는 TRAD-2930(android 죽은 키와 같은 커밋)에서 처리 — 이 커밋에서 안 건드린다. */}
      {hasValues && (
        <div className="mt-2 flex items-center gap-4 text-xs">
          {event.actual !== null && (
            <span>
              <span className="text-muted-foreground">{t('financial_calendar.card.actual')} </span>
              <span className="font-semibold">{formatCalendarValue(event, 'actual')}</span>
            </span>
          )}
          {event.previous !== null && (
            <span>
              <span className="text-muted-foreground">{t('financial_calendar.card.previous')} </span>
              <span>{formatCalendarValue(event, 'previous')}</span>
            </span>
          )}
        </div>
      )}

      {/* CoinMarketCal attribution */}
      {isCrypto && <p className="mt-2 text-2xs text-muted-foreground">Powered by CoinMarketCal</p>}
    </Button>
  );
};
